/**
 * The Finance workspace (workforce write cutover, D-XF-32; packet §26; the
 * board view added in Tide phase 2, 2026-10-04 - see FinanceBoard.tsx).
 *
 * One resident, the Finance slice, in the order a Finance rep asks: who and
 * where · current Finance work · balance, terms, aging · who pays and who
 * decides · approval and UMED · provider and next visit · documents and
 * recordings · recent history · actions (ResidentDossier.tsx). The board is
 * its own view and opens this same dossier under a row. No clinical detail.
 *
 * Every fact says where it came from; a fact with two sources is shown with
 * both. Finance writes only its own notes and the expedited-dispense request
 * that Scheduling decides.
 */
import React, { useCallback, useEffect, useState } from 'react';
import WorkspaceMenu from '../../components/WorkspaceMenu';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  financeApi, outcomeOf, money,
  FinanceScope, ResidentHit, ResidentContext,
} from './financeApi';
import { Context } from './ResidentDossier';
import FinanceBoard from './FinanceBoard';
import '../umed/umed.css';
import './finance.css';
import { shortDate as fmtDate, saleTermsLine, PRIVATE_PAY_LABEL } from './financeApi';
import type { HandoffItem } from './financeApi';
import { SendBackAction } from '../../components/sendBack/SendBackDialog';
import { sendBackPath } from '../../components/sendBack/sendBackApi';
import CutoverReconciliation, { useReconAvailable } from './recon/CutoverReconciliation';

/**
 * Finance's inbox (cutover 2026-09-21): every UMED→Finance handoff that is
 * still open for a resident in scope, oldest first. Until now the handoff
 * obligation was created on the UMED case and listed nowhere Finance looks.
 * Opening one is opening the resident; the obligation itself stays UMED's.
 */
function Handoffs({ onOpen, canSendBack = false }: { onOpen: (residentId: string) => void; canSendBack?: boolean }) {
  const [items, setItems] = useState<HandoffItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Bumped after a send-back closes, so the list re-reads: a returned handoff is no longer Finance's.
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const r = await financeApi.approvals();
        if (!live) return;
        if (r.success && r.data) { setItems(r.data.items); return; }
        setItems(null); setErr(outcomeOf(r));
      } catch { if (live) { setItems(null); setErr('Could not reach the server.'); } }
    })();
    return () => { live = false; };
  }, [nonce]);
  return (
    <section className="um-card" aria-label="Approvals handed to Finance">
      <div className="um-card-head"><div><h2>Handed to Finance</h2><p className="why">UMED approvals and private-pay sales waiting for Finance, oldest first. A private-pay sale comes straight from the Patient Advocate with no UMED, and says so. One approved UMED is one handoff: a correction or reversal completed after it shows on that handoff as "Corrected", never as a second one. Open one to work the resident.</p></div></div>
      {err && <p className="um-notice stop" role="alert" style={{ margin: 12 }}>{err}</p>}
      {!err && items === null && <p className="um-quiet" style={{ padding: 12 }}>Loading…</p>}
      {items && items.length === 0 && <p className="um-quiet" style={{ padding: 12 }}>Nothing waiting. A UMED approval or a private-pay sale handed over in eMerge will appear here.</p>}
      {items && items.length > 0 && (
        <ul className="fn-rows" aria-label="Handoffs">
          {items.map((h) => {
            const pp = h.kind === 'PRIVATE_PAY';
            const terms = pp ? saleTermsLine(h.sale) : null;
            return (
            <li key={h.action_id || h.handoff_id || h.handoff?.id}>
              <button type="button" className="fn-row" onClick={() => onOpen(h.resident.id)}>
                <span className="who">{h.resident.name}</span>
                {pp ? (
                  <>
                    <span className="where">{h.facility_name || 'No current building'} · handed {fmtDate(h.handed_at)}{h.handed_by_name ? ` by ${h.handed_by_name}` : ''}</span>
                    <span className="state">{terms || 'Sale terms not recorded'}{h.finance_status ? ` · ${h.finance_status}` : ' · no Finance account yet'}{h.finance_notes ? ` · ${h.finance_notes} note${h.finance_notes === 1 ? '' : 's'}` : ''}</span>
                    <span className="fn-pp">{h.label || PRIVATE_PAY_LABEL}</span>
                  </>
                ) : (
                  <>
                    <span className="where">{h.facility_name || 'No current building'} · handed {fmtDate(h.handed_at)}{h.umed_operator_name ? ` by ${h.umed_operator_name}` : ''}</span>
                    <span className="state">{h.approval.proven ? `Approved in eMerge${h.approval.noca === 'RECEIVED' ? ' · NOCA received' : ''}` : h.creation_reason ? `Correction completed · ${h.creation_reason.toLowerCase().replace(/_/g, ' ')}` : 'Handed off'}{h.finance_status ? ` · ${h.finance_status}` : ' · no Finance account yet'}{h.finance_notes ? ` · ${h.finance_notes} note${h.finance_notes === 1 ? '' : 's'}` : ''}</span>
                  </>
                )}
                {/* Ruling #1 (2026-10-07): a correction or reversal after the handoff is an amendment on THIS row, never a second row. */}
                {h.corrected && h.corrected.count > 0 && (
                  <span className="fn-corrected" title={`${h.corrected.count} amendment${h.corrected.count === 1 ? '' : 's'} on this handoff`}>
                    Corrected {fmtDate(h.corrected.last_on)}{h.corrected.count > 1 ? ` · ${h.corrected.count} amendments` : ''}
                  </span>
                )}
              </button>
              {/* Only the person who accepted a handoff holds it, so only an ACCEPTED one is offered; the preview and the server decide again. A private-pay handoff never goes back to UMED. */}
              {canSendBack && !pp && h.handoff && h.handoff.state === 'ACCEPTED' && (
                <div className="fn-sendback">
                  <SendBackAction
                    path={sendBackPath.finance(h.handoff.id)} recordLabel={h.resident.name} className="um-open" label="Send back to UMED…"
                    onClosed={(done) => { if (done) setNonce((n) => n + 1); }}
                  />
                </div>
              )}
            </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

const BASE = '/portal/finance';

function Refusal({ title, body }: { title: string; body: string }) {
  return <section className="um-card"><h2>{title}</h2><p className="um-quiet">{body}</p></section>;
}

function SearchBox({ value, onSearch }: { value: string; onSearch: (q: string) => void }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);
  return (
    <form className="um-search" role="search" onSubmit={(e) => { e.preventDefault(); onSearch(draft.trim()); }}>
      <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Resident or building" aria-label="Find a resident" />
      <button type="submit" className="um-open">Find</button>
    </form>
  );
}

/**
 * Three views (Tide phase 2, 2026-10-04), in the URL:
 *   board      the Monday Finance board as a read model - the default
 *   handoffs   ?view=handoffs: the UMED approvals handed to Finance
 *   search     ?view=search[&q=] or ?resident=: find a resident, open the dossier
 * A link that names a resident (a handoff, a pre-board URL) still lands on that
 * resident. A bare ?q= belongs to the board, whose own search box writes it, so
 * the resident search always names its view; inferring "search" from q sent a
 * board search out of the board.
 */
type View = 'board' | 'handoffs' | 'search' | 'recon';
const VIEW_TITLE: Record<View, string> = { board: 'Finance board', handoffs: 'Handed to Finance', search: 'Residents', recon: 'Cutover reconciliation' };

export default function FinanceShell() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const q = params.get('q') || '';
  const residentId = params.get('resident');
  const asked = params.get('view');
  const view: View = asked === 'handoffs' || asked === 'search' || asked === 'board' || asked === 'recon'
    ? asked
    : (residentId ? 'search' : 'board');
  const go = (next: { q?: string; resident?: string | null }) => {
    const p = new URLSearchParams();
    const nq = next.q === undefined ? q : next.q;
    const nr = next.resident === undefined ? residentId : next.resident;
    if (!nr) p.set('view', 'search');
    if (nq) p.set('q', nq);
    if (nr) p.set('resident', nr);
    navigate(`${BASE}?${p}`);
  };
  const setView = (v: View) => navigate(v === 'board' ? BASE : `${BASE}?view=${v}`);

  const [scope, setScope] = useState<FinanceScope | null>(null);
  const [refusal, setRefusal] = useState<{ title: string; body: string } | null>(null);
  const [hits, setHits] = useState<ResidentHit[] | null>(null);
  const [hitsErr, setHitsErr] = useState<string | null>(null);
  const [ctx, setCtx] = useState<ResidentContext | null>(null);
  const [ctxErr, setCtxErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Finance cutover P0: offered only when the server registers the recon routes AND a recon capability is held.
  const reconAvailable = useReconAvailable(Boolean(scope));

  useEffect(() => {
    (async () => {
      try {
        const r = await financeApi.scope();
        if (!r.success || !r.data) { setRefusal(r.code === 'DENY_NO_CAPABILITY' ? { title: 'Not a Finance workspace user', body: 'Your role does not include the Finance resident context. Ask your administrator to ratify a Finance role.' } : { title: 'Could not open Finance', body: outcomeOf(r) }); return; }
        setScope(r.data);
      } catch { setRefusal({ title: 'Could not reach the server', body: 'Try again in a moment.' }); }
    })();
  }, []);

  useEffect(() => {
    if (!scope || !q || view !== 'search') { setHits(null); setHitsErr(null); return; }
    let live = true;
    (async () => {
      const r = await financeApi.search(q);
      if (!live) return;
      if (r.success && r.data) { setHits(r.data.items); setHitsErr(null); return; }
      // Never `[]` on a failure: that renders "No resident matches in your
      // scope", which asserts something about the resident when the truth is
      // that the search did not complete.
      setHits(null);
      setHitsErr(outcomeOf(r));
    })();
    return () => { live = false; };
  }, [scope, q, view]);

  const loadContext = useCallback(async () => {
    if (!residentId || view !== 'search') { setCtx(null); return; }
    setCtxErr(null);
    try {
      const r = await financeApi.resident(residentId);
      if (!r.success || !r.data) { setCtx(null); setCtxErr(r.code === 'NOT_FOUND' ? 'No such resident here. They may be in a building outside your scope.' : outcomeOf(r)); return; }
      setCtx(r.data);
    } catch { setCtx(null); setCtxErr('Could not reach the server.'); }
  }, [residentId, view]);
  useEffect(() => { setNotice(null); if (scope) loadContext(); }, [scope, loadContext]);

  const onChanged = async (msg: string) => { setNotice(msg); await loadContext(); };

  if (refusal) return <div className="um"><main className="um-main one"><Refusal title={refusal.title} body={refusal.body} /></main></div>;
  if (!scope) return <div className="um"><main className="um-main one"><section className="um-card"><p className="um-quiet">Opening Finance…</p></section></main></div>;

  const approvalsOpen = scope.counts.approvals_open;
  return (
    <div className="um board-host">
      <WorkspaceMenu current="FINANCE" />
      <header className="um-bar">
        <div className="um-bar-inner">
          <div className="um-ident">
            <div className="um-where">
              <span className="um-eyebrow">eMerge · Finance</span>
              <h1>{VIEW_TITLE[view]}</h1>
              <span className="um-sub">{scope.enterprise ? 'Every building' : `${scope.facility_count} ${scope.facility_count === 1 ? 'building' : 'buildings'}`} · {scope.counts.expedited_pending} expedited {scope.counts.expedited_pending === 1 ? 'request' : 'requests'} with Scheduling</span>
            </div>
          </div>
        </div>
      </header>
      <main className={`um-main${view === 'search' ? '' : ' one'}${view === 'board' ? ' board-main' : ''}`}>
        <nav className="sc-views" aria-label="Finance views" style={view === 'search' ? { gridColumn: '1 / -1' } : undefined}>
          <button type="button" className="um-open" aria-pressed={view === 'board'} onClick={() => setView('board')}>Board</button>
          <button type="button" className="um-open" aria-pressed={view === 'handoffs'} onClick={() => setView('handoffs')}>Handed to Finance{typeof approvalsOpen === 'number' && approvalsOpen > 0 ? ` · ${approvalsOpen}` : ''}</button>
          <button type="button" className="um-open" aria-pressed={view === 'search'} onClick={() => setView('search')}>Search</button>
          {(reconAvailable || view === 'recon') && <button type="button" className="um-open" aria-pressed={view === 'recon'} onClick={() => setView('recon')}>Cutover reconciliation</button>}
        </nav>
        {view === 'board' && <FinanceBoard scope={scope} />}
        {view === 'recon' && <CutoverReconciliation />}
        {view === 'handoffs' && <Handoffs onOpen={(id) => go({ resident: id })} canSendBack={Boolean(scope.can?.send_back)} />}
        {view === 'search' && (
          <>
            <div className="um-list">
              <SearchBox value={q} onSearch={(next) => go({ q: next, resident: null })} />
              {!q && <p className="um-quiet">Find a resident by name, or a building by name, to open their Finance context.</p>}
              {q && hitsErr && <p className="um-notice stop" role="alert">{hitsErr}</p>}
              {q && !hitsErr && hits === null && <p className="um-quiet">Searching…</p>}
              {q && !hitsErr && hits && hits.length === 0 && <p className="um-quiet">No resident matches in your scope.</p>}
              {hits && hits.length > 0 && (
                <ul className="fn-rows" aria-label="Residents">
                  {hits.map((h) => (
                    <li key={h.id} className={h.id === residentId ? 'selected' : ''}>
                      <button type="button" className="fn-row" onClick={() => go({ resident: h.id })} aria-current={h.id === residentId ? 'true' : undefined}>
                        <span className="who">{h.name}</span>
                        <span className="where">{h.facility_name || 'No current building'}</span>
                        <span className="state">{h.finance_status || 'No Finance account'}{h.remaining_amount !== null ? ` · ${money(h.remaining_amount)} remaining` : ''}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <aside className="um-context">
              {ctxErr && <section className="um-card"><p className="um-notice stop" role="alert">{ctxErr}</p></section>}
              {!ctxErr && !ctx && <section className="um-card"><p className="um-empty">Select a resident to see their Finance context.</p></section>}
              {ctx && <Context ctx={ctx} scope={scope} notice={notice} onChanged={onChanged} />}
            </aside>
          </>
        )}
      </main>
    </div>
  );
}
